import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, X, Package, Download } from "lucide-react";
import { toast } from "sonner";
import { API, statusColors, formatCurrency } from "./utils";

function MaterialsContent({ projectId, materials, onRefresh, canWrite, token }) {
  const [activeTab, setActiveTab] = useState("materials");
  const [materialDocs, setMaterialDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [showDocForm, setShowDocForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Material form state
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialFormData, setMaterialFormData] = useState({
    material_name: "",
    unit: "pcs",
    quantity_required: 0,
    quantity_received: 0,
    quantity_used: 0,
    unit_price: 0,
    status: "Pending"
  });

  const defaultItem = { material_name: "", quantity: 1, cost: 0 };
  const [docFormData, setDocFormData] = useState({
    title: "Materials Document",
    items: [{ ...defaultItem }],
    notes: ""
  });

  useEffect(() => { fetchMaterialDocs(); }, [projectId]);

  const fetchMaterialDocs = async () => {
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API}/materials-documents/project/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMaterialDocs(res.data);
    } catch (error) { console.error("Failed to fetch material docs", error); }
    finally { setLoadingDocs(false); }
  };

  const resetMaterialForm = () => {
    setMaterialFormData({
      material_name: "",
      unit: "pcs",
      quantity_required: 0,
      quantity_received: 0,
      quantity_used: 0,
      unit_price: 0,
      status: "Pending"
    });
    setEditingMaterial(null);
    setShowMaterialForm(false);
  };

  const handleEditMaterial = (material) => {
    setMaterialFormData({
      material_name: material.material_name,
      unit: material.unit || "pcs",
      quantity_required: material.quantity_required || 0,
      quantity_received: material.quantity_received || 0,
      quantity_used: material.quantity_used || 0,
      unit_price: material.unit_price || 0,
      status: material.status || "Pending"
    });
    setEditingMaterial(material);
    setShowMaterialForm(true);
  };

  const handleSubmitMaterial = async (e) => {
    e.preventDefault();
    if (!materialFormData.material_name) {
      toast.error("Please enter material name");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...materialFormData,
        total_price: materialFormData.quantity_required * materialFormData.unit_price
      };
      if (editingMaterial) {
        await axios.put(`${API}/projects/${projectId}/materials/${editingMaterial.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Material updated!");
      } else {
        await axios.post(`${API}/projects/${projectId}/materials`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Material added!");
      }
      resetMaterialForm();
      onRefresh();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save material"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm("Delete this material?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/materials/${materialId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Material deleted");
      onRefresh();
    } catch (error) { toast.error("Failed to delete material"); }
  };

  const resetDocForm = () => {
    setDocFormData({ title: "Materials Document", items: [{ ...defaultItem }], notes: "" });
    setEditingDoc(null);
    setShowDocForm(false);
  };

  const handleEditDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/materials-documents/${doc.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDocFormData({
        title: res.data.title,
        items: res.data.items.map(i => ({ material_name: i.material_name, quantity: i.quantity, cost: i.cost })),
        notes: res.data.notes || ""
      });
      setEditingDoc(doc);
      setShowDocForm(true);
    } catch (error) { toast.error("Failed to load document"); }
  };

  const handleSubmitDoc = async (e) => {
    e.preventDefault();
    if (docFormData.items.length === 0 || !docFormData.items[0].material_name) {
      toast.error("Please add at least one material");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { project_id: projectId, ...docFormData };
      if (editingDoc) {
        await axios.put(`${API}/materials-documents/${editingDoc.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Document updated!");
      } else {
        await axios.post(`${API}/materials-documents`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Document created!");
      }
      resetDocForm();
      fetchMaterialDocs();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save document"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm("Delete this materials document?")) return;
    try {
      await axios.delete(`${API}/materials-documents/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Document deleted");
      fetchMaterialDocs();
    } catch (error) { toast.error("Failed to delete"); }
  };

  const handleDownloadDocPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/materials-documents/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Materials_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) { toast.error("Failed to download PDF"); }
  };

  const handleDocItemChange = (index, field, value) => {
    const newItems = [...docFormData.items];
    newItems[index] = { ...newItems[index], [field]: field === 'quantity' || field === 'cost' ? parseFloat(value) || 0 : value };
    setDocFormData({ ...docFormData, items: newItems });
  };

  const addDocItem = () => setDocFormData({ ...docFormData, items: [...docFormData.items, { ...defaultItem }] });
  const removeDocItem = (index) => {
    if (docFormData.items.length > 1) {
      setDocFormData({ ...docFormData, items: docFormData.items.filter((_, i) => i !== index) });
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
  const calculateDocTotal = () => docFormData.items.reduce((sum, item) => sum + (item.quantity * item.cost), 0);

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="materials">Materials Tracking</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-material-docs">Materials Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="materials">
          <div className="space-y-4">
            <div className="flex justify-end">
              {canWrite && !showMaterialForm && (
                <Button size="sm" onClick={() => setShowMaterialForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="add-material-btn">
                  <Plus className="h-4 w-4 mr-1" />Add Material
                </Button>
              )}
            </div>
            
            {showMaterialForm && (
              <Card className="p-5 border-brillien-200 bg-brillien-50/50">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">{editingMaterial ? "Edit Material" : "Add New Material"}</h4>
                  <Button variant="ghost" size="sm" onClick={resetMaterialForm}><X className="h-4 w-4" /></Button>
                </div>
                <form onSubmit={handleSubmitMaterial} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Material Name *</Label><Input value={materialFormData.material_name} onChange={e => setMaterialFormData({...materialFormData, material_name: e.target.value})} required /></div>
                    <div>
                      <Label>Unit</Label>
                      <Select value={materialFormData.unit} onValueChange={v => setMaterialFormData({...materialFormData, unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pcs">Pieces</SelectItem>
                          <SelectItem value="kg">Kilograms</SelectItem>
                          <SelectItem value="m">Meters</SelectItem>
                          <SelectItem value="sqm">Square Meters</SelectItem>
                          <SelectItem value="sqft">Square Feet</SelectItem>
                          <SelectItem value="ltr">Liters</SelectItem>
                          <SelectItem value="bags">Bags</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div><Label>Qty Required</Label><Input type="number" value={materialFormData.quantity_required} onChange={e => setMaterialFormData({...materialFormData, quantity_required: Number(e.target.value)})} /></div>
                    <div><Label>Qty Received</Label><Input type="number" value={materialFormData.quantity_received} onChange={e => setMaterialFormData({...materialFormData, quantity_received: Number(e.target.value)})} /></div>
                    <div><Label>Qty Used</Label><Input type="number" value={materialFormData.quantity_used} onChange={e => setMaterialFormData({...materialFormData, quantity_used: Number(e.target.value)})} /></div>
                    <div><Label>Unit Price (₹)</Label><Input type="number" value={materialFormData.unit_price} onChange={e => setMaterialFormData({...materialFormData, unit_price: Number(e.target.value)})} /></div>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={materialFormData.status} onValueChange={v => setMaterialFormData({...materialFormData, status: v})}>
                      <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Ordered">Ordered</SelectItem>
                        <SelectItem value="Received">Received</SelectItem>
                        <SelectItem value="In Use">In Use</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={resetMaterialForm}>Cancel</Button>
                    <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">
                      {submitting ? "Saving..." : (editingMaterial ? "Update" : "Add Material")}
                    </Button>
                  </div>
                </form>
              </Card>
            )}
            
            {materials.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No materials listed</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Used</TableHead>
                    <TableHead>Total Price</TableHead>
                    <TableHead>Status</TableHead>
                    {canWrite && <TableHead className="w-20">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materials.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.material_name}</TableCell>
                      <TableCell>{m.quantity_required} {m.unit}</TableCell>
                      <TableCell>{m.quantity_received}</TableCell>
                      <TableCell>{m.quantity_used}</TableCell>
                      <TableCell>₹{m.total_price?.toLocaleString() || 0}</TableCell>
                      <TableCell><Badge className={statusColors[m.status]}>{m.status}</Badge></TableCell>
                      {canWrite && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditMaterial(m)}><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteMaterial(m.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-900">Materials Documents</h4>
              {canWrite && !showDocForm && (
                <Button onClick={() => setShowDocForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="create-material-doc-btn">
                  <Plus className="h-4 w-4 mr-2" />New Document
                </Button>
              )}
            </div>

            {showDocForm && (
              <Card className="p-5 border-brillien-200 bg-brillien-50/50">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">{editingDoc ? `Edit - ${editingDoc.id}` : "New Materials Document"}</h4>
                  <Button variant="ghost" size="sm" onClick={resetDocForm}><X className="h-4 w-4" /></Button>
                </div>
                <form onSubmit={handleSubmitDoc} className="space-y-4">
                  <div><Label>Title *</Label><Input value={docFormData.title} onChange={e => setDocFormData({...docFormData, title: e.target.value})} required /></div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-100">
                          <TableHead className="w-10">S.No</TableHead>
                          <TableHead className="min-w-[200px]">Material Name</TableHead>
                          <TableHead className="w-24">Quantity</TableHead>
                          <TableHead className="w-28">Cost (₹)</TableHead>
                          <TableHead className="w-28">Total (₹)</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {docFormData.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-center font-medium">{idx + 1}</TableCell>
                            <TableCell><Input value={item.material_name} onChange={e => handleDocItemChange(idx, "material_name", e.target.value)} placeholder="Material name" className="border-slate-300" /></TableCell>
                            <TableCell><Input type="number" value={item.quantity} onChange={e => handleDocItemChange(idx, "quantity", e.target.value)} className="border-slate-300 text-center" /></TableCell>
                            <TableCell><Input type="number" value={item.cost} onChange={e => handleDocItemChange(idx, "cost", e.target.value)} className="border-slate-300 text-right" /></TableCell>
                            <TableCell className="text-right font-medium">₹{(item.quantity * item.cost).toLocaleString()}</TableCell>
                            <TableCell><Button type="button" variant="ghost" size="sm" onClick={() => removeDocItem(idx)} disabled={docFormData.items.length === 1}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex justify-between items-center">
                    <Button type="button" variant="outline" size="sm" onClick={addDocItem}><Plus className="h-4 w-4 mr-1" />Add Item</Button>
                    <div className="text-right"><span className="text-slate-600">Grand Total: </span><span className="font-bold text-lg">₹{calculateDocTotal().toLocaleString()}</span></div>
                  </div>
                  <div><Label>Notes</Label><Textarea value={docFormData.notes} onChange={e => setDocFormData({...docFormData, notes: e.target.value})} rows={2} placeholder="Additional notes..." /></div>
                  <div className="flex gap-2 pt-2">
                    <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Saving..." : editingDoc ? "Update" : "Create"}</Button>
                    <Button type="button" variant="outline" onClick={resetDocForm}>Cancel</Button>
                  </div>
                </form>
              </Card>
            )}

            {loadingDocs ? (
              <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brillien-600" /></div>
            ) : materialDocs.length === 0 && !showDocForm ? (
              <div className="text-center py-12 text-slate-500"><Package className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>No materials documents yet</p></div>
            ) : !showDocForm && (
              <div className="space-y-3">
                {materialDocs.map(doc => (
                  <Card key={doc.id} className="p-4 border-slate-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2"><span className="font-mono text-sm text-brillien-600">{doc.id}</span><Badge className={doc.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>{doc.status}</Badge></div>
                        <h4 className="font-semibold mt-1">{doc.title}</h4>
                        <p className="text-sm text-slate-600">{doc.items?.length || 0} items</p>
                        <p className="text-lg font-bold text-brillien-600 mt-1">Total: ₹{(doc.total_cost || 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500 mt-1">Created: {formatDate(doc.created_at)} | Updated: {formatDate(doc.updated_at)}</p>
                      </div>
                      <div className="flex gap-2">
                        {canWrite && doc.status === "draft" && <Button variant="outline" size="sm" onClick={() => handleEditDoc(doc)} className="text-blue-600"><Edit className="h-4 w-4 mr-1" />Edit</Button>}
                        <Button variant="outline" size="sm" onClick={() => handleDownloadDocPdf(doc.id)} className="text-brillien-600"><Download className="h-4 w-4 mr-1" />PDF</Button>
                        {canWrite && <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ DAILY UPDATES CONTENT ============

export { MaterialsContent };
